import { z } from "zod";
import { businessRoleSchema } from "../enums.ts";
import { uuidSchema } from "./common.ts";

// ---------------------------------------------------------------------------
// Profile photos (migration 0029)
// ---------------------------------------------------------------------------

/** A profile photo is a square JPEG of this many pixels on each side. The browser makes it. */
export const PROFILE_PHOTO_SIDE = 512;
/** The largest JPEG file the API takes for one profile photo, in bytes. */
export const PROFILE_PHOTO_MAX_BYTES = 256 * 1024;
/** The most photos one save of the Team photo tool can carry. */
export const TEAM_PHOTO_MAX_PHOTOS = 40;

/**
 * Whose photo it is. `participant` is a participant of the business (the id is the participant
 * id). `staff` is a person on the team: owner, admin or staff (the id is the user id). `guardian`
 * is a parent at the business (the id is the user id; migration 0032): the account of a family
 * with a participant here that is not archived, a second adult on such a family, a member whose
 * membership is not `left`, or the account of a number on the business's member list.
 */
export const photoSubjectKindSchema = z.enum(["participant", "staff", "guardian"]);
export type PhotoSubjectKind = z.infer<typeof photoSubjectKindSchema>;

export const photoSubjectSchema = z.object({ kind: photoSubjectKindSchema, id: uuidSchema });
export type PhotoSubject = z.infer<typeof photoSubjectSchema>;

/** A JPEG file as base64, no `data:` prefix. */
export const jpegBase64Schema = z
  .string()
  .min(16)
  .max(Math.ceil(PROFILE_PHOTO_MAX_BYTES / 3) * 4, "The photo is too large")
  .regex(/^[A-Za-z0-9+/]+={0,2}$/, "Send the photo as base64");

/**
 * One person the owner can give a photo to, in the Team photo tool and the member profile.
 * `photoUrl` is a signed address that works for some hours only: never store it.
 */
export const photoPersonSchema = z.object({
  kind: photoSubjectKindSchema,
  id: uuidSchema,
  name: z.string(),
  /** The participant's group, for telling two people with the same name apart. */
  groupName: z.string().nullable(),
  /** The role of a person on the team. Null for a participant. */
  role: businessRoleSchema.nullable(),
  photoUrl: z.string().nullable(),
  /** The family said "no profile photo". The owner cannot add one. Always false for the team. */
  photosRefused: z.boolean(),
});
export type PhotoPerson = z.infer<typeof photoPersonSchema>;

/** `GET /v1/owner/businesses/:businessId/photos/people`: everyone who can have a photo, by name. */
export const photoPeopleResponseSchema = z.object({ people: z.array(photoPersonSchema) });
export type PhotoPeopleResponse = z.infer<typeof photoPeopleResponseSchema>;

/**
 * `POST /v1/owner/businesses/:businessId/photos`: one or more photos, each for one person. A photo
 * replaces the person's earlier photo. All or nothing: one refused photo refuses the save.
 */
export const saveProfilePhotosRequestSchema = z.object({
  photos: z
    .array(z.object({ subject: photoSubjectSchema, jpeg: jpegBase64Schema }))
    .min(1)
    .max(TEAM_PHOTO_MAX_PHOTOS)
    .refine(
      (photos) => new Set(photos.map((photo) => `${photo.subject.kind}:${photo.subject.id}`)).size === photos.length,
      "Each person can have one photo in a save",
    ),
});
export type SaveProfilePhotosRequest = z.infer<typeof saveProfilePhotosRequestSchema>;

export const photoSubjectParamSchema = z.object({
  businessId: uuidSchema,
  kind: photoSubjectKindSchema,
  id: uuidSchema,
});

/** `PUT /v1/me/businesses/:businessId/photo` and `PUT /v1/me/participants/:id/photo`. */
export const setPhotoRequestSchema = z.object({ jpeg: jpegBase64Schema });
export type SetPhotoRequest = z.infer<typeof setPhotoRequestSchema>;

/** The answer to every write of one photo. Null after a remove. */
export const photoResponseSchema = z.object({ photoUrl: z.string().nullable() });
export type PhotoResponse = z.infer<typeof photoResponseSchema>;

/**
 * `PUT /v1/me/participants/:id/photo-consent`. `photosAllowed: false` deletes every photo of the
 * participant at once and stops any business from adding one.
 */
export const photoConsentRequestSchema = z.object({ photosAllowed: z.boolean() });
export type PhotoConsentRequest = z.infer<typeof photoConsentRequestSchema>;

/**
 * `GET` and `PUT /v1/me/photo-consent`: a profile photo of me as a parent. `false` deletes it at
 * every business at once and stops any business from adding one.
 */
export const photoConsentResponseSchema = z.object({ photosAllowed: z.boolean() });
export type PhotoConsentResponse = z.infer<typeof photoConsentResponseSchema>;
